import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  sendPasswordResetEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword as fbUpdatePassword,
  verifyBeforeUpdateEmail,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { getTenantHeaders } from '@/lib/tenant-context';
import { getApiUrl } from '@/lib/api-config';

// Types for enhanced authentication
import { UserProfile, Tenant, Role, Permission } from '@/types/auth';

import { AuthContextType } from '@/types/auth';

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// Helper function to get tenant from URL/subdomain
const getTenantFromContext = (): string | null => {
  const host = window.location.hostname.toLowerCase();
  const parts = host.split('.');
  // dev: slug.localhost
  if (parts.length >= 2 && parts[parts.length - 1] === 'localhost') {
    return parts[0];
  }
  // prod: slug.domain.tld (3+)
  if (parts.length >= 3) {
    return parts[0];
  }
  // dev fallback: localhost?tenant=slug
  const qp = new URLSearchParams(window.location.search).get('tenant');
  return qp || null;
};

// Helper function to get auth token
const getAuthToken = async (): Promise<string | null> => {
  const user = auth.currentUser;
  if (!user) return null;
  return await user.getIdToken();
};

// API helper functions
const apiCall = async (endpoint: string, options: RequestInit = {}) => {
  const token = await getAuthToken();
  const tenantHeaders = getTenantHeaders();
  const fullUrl = getApiUrl(endpoint);
  
  /*console.log('🔍 AuthContext API Call Debug:', {
    endpoint: fullUrl,
    hasToken: !!token,
    tenantHeaders,
    isDev: import.meta.env.DEV
  });*/
  
  const response = await fetch(fullUrl, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...tenantHeaders,
      ...options.headers,
    },
  });
  
  /*console.log('🔍 AuthContext API Response:', {
    status: response.status,
    statusText: response.statusText,
    url: response.url
  });*/
  
  if (!response.ok) {
    const responseText = await response.text();
    console.log('🔍 AuthContext API Error Response:', responseText);
    
    try {
      const error = JSON.parse(responseText);
      throw new Error(error.error || 'API request failed');
    } catch (parseError) {
      throw new Error(`API request failed: ${response.status} - ${responseText.substring(0, 100)}`);
    }
  }
  
  return response.json();
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [userRoles, setUserRoles] = useState<Role[]>([]);
  const [userPermissions, setUserPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [operationLoading, setOperationLoading] = useState(false);

  // Load user context after Firebase authentication
  const loadUserContext = useCallback(async () => {
    try {
      const tenantSlug = getTenantFromContext();
      if (!tenantSlug) {
        console.error('No tenant context found');
        return;
      }

      // Get user profile and tenant information
      const profileData = await apiCall('/auth/me');
      
      setUserProfile(profileData.user);
      setCurrentTenant(profileData.tenant);
      setUserRoles(profileData.roles || []);
      setUserPermissions(profileData.permissions || []);
    } catch (error) {
      console.error('Failed to load user context:', error);
      // If user profile doesn't exist, we'll handle it during login
    }
  }, []);

  const signup = async (email: string, password: string) => {
    setOperationLoading(true);
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      setCurrentUser(user);
    } finally {
      setOperationLoading(false);
    }
  };

  const login = async (email: string, password: string, rememberMe = true) => {
    setOperationLoading(true);
    try {
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      
      // Load user context after successful login
      await loadUserContext();
      
      setCurrentUser(user);
    } finally {
      setOperationLoading(false);
    }
  };

  const logout = async () => {
    setOperationLoading(true);
    try {
      await signOut(auth);
      setCurrentUser(null);
      setUserProfile(null);
      setCurrentTenant(null);
      setUserRoles([]);
      setUserPermissions([]);
    } finally {
      setOperationLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    setOperationLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
    } finally {
      setOperationLoading(false);
    }
  };

  const reauthenticate = async (currentPassword: string) => {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error('No authenticated user');
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    setOperationLoading(true);
    try {
      await reauthenticate(currentPassword);
      if (!auth.currentUser) throw new Error('No authenticated user');
      await fbUpdatePassword(auth.currentUser, newPassword);
    } finally {
      setOperationLoading(false);
    }
  };

  const changeEmail = async (currentPassword: string, newEmail: string) => {
    setOperationLoading(true);
    try {
      await reauthenticate(currentPassword);
      if (!auth.currentUser) throw new Error('No authenticated user');

      // Prefer verified update flow to avoid silent failures and ensure ownership
      const actionCodeSettings = {
        url: `${window.location.origin}/account-settings`,
        handleCodeInApp: false,
      };
      await verifyBeforeUpdateEmail(auth.currentUser, newEmail, actionCodeSettings);
      // Do not mutate local email; it updates after the user confirms via the email link
    } finally {
      setOperationLoading(false);
    }
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!userProfile) throw new Error('No user profile');
    
    setOperationLoading(true);
    try {
      const response = await apiCall('/auth/me', {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      
      setUserProfile(response.user);
    } finally {
      setOperationLoading(false);
    }
  };

  const refreshUserData = async () => {
    if (currentUser) {
      await loadUserContext();
    }
  };

  // Permission checking functions
  const hasPermission = useCallback((permission: string): boolean => {
    return userPermissions.some(p => p.key === permission);
  }, [userPermissions]);

  const hasRole = useCallback((role: string): boolean => {
    return userRoles.some(r => r.name === role);
  }, [userRoles]);

  const canAccessFeature = useCallback((feature: string): boolean => {
    if (!currentTenant?.plan_features) return false;
    return currentTenant.plan_features[feature] === true;
  }, [currentTenant]);

  const canAccessAllFeatures = useCallback((features: string[]): boolean => {
    return (features || []).every(f => canAccessFeature(f));
  }, [canAccessFeature]);

  const canAccessAnyFeature = useCallback((features: string[]): boolean => {
    return (features || []).some(f => canAccessFeature(f));
  }, [canAccessFeature]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        // Load user context if we have a tenant context
        const tenantSlug = getTenantFromContext();
        if (tenantSlug) {
          await loadUserContext();
        }
      } else {
        // User logged out, clear all state
        setCurrentUser(null);
        setUserProfile(null);
        setCurrentTenant(null);
        setUserRoles([]);
        setUserPermissions([]);
      }
      setLoading(false);
    });
    
    setPersistence(auth, browserLocalPersistence).catch(console.error);
    return unsub;
  }, [loadUserContext]);

  const value: AuthContextType = {
    currentUser,
    userProfile,
    currentTenant,
    userRoles,
    userPermissions,
    tenantPlan: currentTenant?.plan || null,
    loading,
    operationLoading,
    login,
    signup,
    logout,
    resetPassword,
    changePassword,
    changeEmail,
    hasPermission,
    hasRole,
    canAccessFeature,
    canAccessAllFeatures,
    canAccessAnyFeature,
    updateProfile,
    refreshUserData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}


